import { SparkleIcon } from '@phosphor-icons/react';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import * as React from 'react';

import { Calendar } from '@/components/ui/calendar';
import {
  applyTimeToDate,
  exactTimeValueFromDate,
  viewerTimeZoneName,
} from '@/lib/posts/time-select';
import { cn } from '@/lib/utils';

export interface SchedulePickerProps {
  value?: Date;
  pendingTime: string;
  onPendingTimeChange: (value: string) => void;
  onDateChange: (date: Date) => void | Promise<void>;
  onTimeChange?: (date: Date) => void | Promise<void>;
  minDate?: Date;
  markedDates?: Date[];
  disabled?: boolean;
  timeLabel?: string;
  /** `false` for date-only fields: hides the time row, keeps the quick picks. */
  showTime?: boolean;
  onAutoSchedule?: () => void | Promise<void>;
  autoScheduleHint?: string;
  /** Opens the account-level posting-window preference when Auto-schedule is available. */
  onUpdateAutoScheduleSettings?: () => void;
  /**
   * A time Mora suggests from the workspace's results (`useSuggestedPostTime`).
   * Shown first, in the AI-suggestion treatment (skill purple, dashed), with
   * its basis; one press applies it. When present it replaces "Next open slot".
   */
  suggestion?: { at: Date; basis: string; onApply: () => void | Promise<void> } | null;
}

const QUICK_PICKS: { label: string; days: number }[] = [
  { label: 'Today', days: 0 },
  { label: 'Tomorrow', days: 1 },
  { label: 'Next week', days: 7 },
];

/**
 * THE scheduling picker — one component behind every surface that puts a post
 * on a date: the inspector's plan row, the publish split-button's Schedule
 * panel, and a single platform's own send time. It replaced three different
 * controls, one of which was a `<DatePicker>` (a trigger button that opens its
 * OWN popover) mounted inside a popover, which is what rendered as a stray
 * card floating over the panel.
 *
 * Composition follows shadcn's documented calendar
 * (ui.shadcn.com/docs/components/base/calendar — "Presets" / "Date and Time
 * Picker") and, more importantly, its restraint: a padded grid, ONE hairline
 * rule, then a footer. The only bordered objects on the whole surface are the
 * preset buttons, because they are the only things you press. Everything else
 * — grid, time field, container — is flat and shares the popover's own
 * surface, so there are no boxes inside boxes.
 *
 * The popover that hosts this should pass `p-0`; the picker owns its padding.
 */
export function SchedulePicker({
  value,
  pendingTime,
  onPendingTimeChange,
  onDateChange,
  onTimeChange,
  minDate,
  markedDates,
  disabled,
  timeLabel = 'Time',
  showTime = true,
  onAutoSchedule,
  autoScheduleHint,
  onUpdateAutoScheduleSettings,
  suggestion,
}: SchedulePickerProps): JSX.Element {
  const selectedTime = value ? exactTimeValueFromDate(value) : pendingTime;
  const zoneName = viewerTimeZoneName(value);
  // The grid must follow a quick-pick — "Next week" tells you nothing if you
  // then have to page forward to see where it landed.
  const [month, setMonth] = React.useState<Date>(() => value ?? new Date());
  React.useEffect(() => {
    if (value) setMonth(value);
  }, [value]);

  const applyDate = (nextDay: Date): void => {
    if (disabled) return;
    const next = applyTimeToDate(nextDay, selectedTime);
    setMonth(next);
    void onDateChange(next);
  };

  return (
    <div className="w-72">
      {suggestion ? (
        <div className="border-b border-border/40 p-3">
          <button
            type="button"
            disabled={disabled}
            onClick={() => void suggestion.onApply()}
            className="flex w-full items-start gap-2 rounded-md border border-dashed border-skill/50 bg-skill/[0.06] px-2.5 py-2 text-left transition-colors hover:bg-skill/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
          >
            <SparkleIcon
              className="mt-0.5 size-3.5 shrink-0 text-skill"
              weight="regular"
              aria-hidden="true"
            />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-caption font-medium text-foreground">
                Suggested · {format(suggestion.at, 'EEE, MMM d · h:mm a')}
              </span>
              <span className="text-detail leading-snug text-muted-foreground">
                {suggestion.basis}
              </span>
            </span>
          </button>
        </div>
      ) : null}
      <div className="p-3">
        <Calendar
          initialFocus
          mode="single"
          month={month}
          onMonthChange={setMonth}
          selected={value}
          onSelect={(newDate) => {
            if (!newDate) return;
            applyDate(newDate);
          }}
          modifiers={markedDates && markedDates.length ? { marked: markedDates } : undefined}
          // `startOfDay` matters: calendar days are midnight, so comparing them
          // against a `minDate` of `new Date()` disabled TODAY — which also
          // made the "Today" quick-pick dead on arrival.
          disabled={(date) => (minDate ? date < startOfDay(minDate) : false)}
          // Inline custom property, not a `[--cell-size:…]` class — see
          // calendar.tsx: the primitive's own default is responsive, and a
          // `md:` rule outranks an unprefixed utility from a caller.
          cellSize="2.25rem"
          className="w-full p-0"
          classNames={{
            root: 'w-full',
            month: 'flex w-full flex-col gap-3',
            // `bg-transparent` must be repeated here: `classNames` entries
            // REPLACE the primitive's, they don't merge, so dropping it would
            // hand the row back to the app's global zebra-striping for `<tr>`.
            week: 'mt-1 flex w-full bg-transparent',
            weekday: 'flex-1 bg-transparent font-normal text-muted-foreground select-none',
            day: 'group/day relative aspect-square h-full flex-1 p-0 text-center select-none',
          }}
        />
      </div>

      <div className="space-y-2.5 border-t border-border/40 p-3">
        {/* Native `<input type="time">` — shadcn's own date-and-time-picker
            composition, and the honest one. The 48-option half-hour `<Select>`
            it replaces had no entry for an off-grid time, so a post planned for
            1:56 PM displayed "2:00 PM" in the very same popover whose trigger
            read "Planned for … 1:56 PM": two different times, one panel.
            Borderless — the label/value row is a field, not a control to press,
            and every extra outline here was another box inside the box. */}
        {showTime ? (
          <label className="flex items-center justify-between gap-2">
            <span className="text-caption text-muted-foreground">{timeLabel}</span>
            <input
              type="time"
              value={selectedTime}
              disabled={disabled}
              aria-label={timeLabel}
              onChange={(event) => {
                const nextTime = event.target.value;
                if (!nextTime) return;
                onPendingTimeChange(nextTime);
                if (!value || disabled || !onTimeChange) return;
                void onTimeChange(applyTimeToDate(value, nextTime));
              }}
              className="-mr-1 rounded bg-transparent px-1 py-0.5 text-caption font-medium tabular-nums text-foreground outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:hidden"
            />
          </label>
        ) : null}
        {showTime && zoneName ? (
          <p className="text-detail text-muted-foreground">Times are in {zoneName}.</p>
        ) : null}

        {/* Three shortcuts, not five. "In 3 days" and "In 2 weeks" are shadcn
            demo filler; a social post is scheduled for today, tomorrow, or
            roughly next week. "Next open slot" leads because it is the only
            one that also picks the TIME, from the user's own posting window. */}
        <div className="flex flex-col gap-1.5">
          {onAutoSchedule && !suggestion ? (
            // `w-full`, not `flex-1`. This button is the SOLE item of a
            // column flex container (its sibling is the row below, a
            // separate div) — `flex-1` is `flex: 1 1 0%`, and in a column
            // container the basis is on the height axis, so it collapsed the
            // button to its content's min height (~22px) and silently
            // ignored `h-8` (~32px). `flex-1` is correct for the row below,
            // where three siblings share the main (horizontal) axis instead.
            <>
              <Chip
                disabled={disabled}
                onClick={() => void onAutoSchedule()}
                title={autoScheduleHint}
                className="w-full flex-none"
              >
                <SparkleIcon className="size-3.5 shrink-0" weight="duotone" aria-hidden="true" />
                Next open slot
              </Chip>
              {autoScheduleHint && onUpdateAutoScheduleSettings ? (
                <div className="flex items-center justify-between gap-2 px-0.5">
                  <span className="text-detail text-muted-foreground">{autoScheduleHint}</span>
                  <button
                    type="button"
                    onClick={onUpdateAutoScheduleSettings}
                    className="shrink-0 text-detail text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Update settings
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          <div className="flex gap-1.5">
            {QUICK_PICKS.map((pick) => {
              const target = addDays(startOfDay(new Date()), pick.days);
              return (
                <Chip
                  key={pick.label}
                  disabled={disabled || (minDate ? target < startOfDay(minDate) : false)}
                  active={value ? isSameDay(value, target) : false}
                  onClick={() => applyDate(target)}
                >
                  {pick.label}
                </Chip>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({
  children,
  active,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }): JSX.Element {
  return (
    <button
      type="button"
      data-active={active}
      className={cn(
        'inline-flex h-8 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-border/60 bg-transparent px-2 text-caption font-medium text-foreground/80 transition-colors',
        'hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:pointer-events-none disabled:opacity-40',
        'data-[active=true]:border-foreground/40 data-[active=true]:bg-muted data-[active=true]:text-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export default SchedulePicker;
